"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var click_outside_1 = require("../click-outside");
var react_popper_1 = require("react-popper");
var react_transition_group_1 = require("react-transition-group");
var react_portal_1 = require("react-portal");
var Popover_st_css_1 = require("./Popover.st.css");
var modifiers_1 = require("./modifiers");
var filter_data_props_1 = require("../../utils/filter-data-props");
var uniqueId = require('lodash/uniqueId');
var utils_1 = require("../../utils");
var PopoverContext_1 = require("./PopoverContext");
var helpers_1 = require("./helpers");
var getAppendToElement_1 = require("./utils/getAppendToElement");
var classnames_1 = require("classnames");
// This is here and not in the test setup because we don't want consumers to need to run it as well
var testId;
var isTestEnv = process.env.NODE_ENV === 'test';
if (isTestEnv && typeof document !== 'undefined' && !document.createRange) {
    helpers_1.popoverTestUtils.createRange();
}
var attachClasses = function (node, classnames) {
    var _a;
    return node && (_a = node.classList).add.apply(_a, classnames.split(' '));
};
var detachClasses = function (node, classnames) {
    var _a;
    return node && (_a = node.classList).remove.apply(_a, classnames.split(' '));
};
var shouldAnimatePopover = function (_a) {
    var timeout = _a.timeout;
    if (typeof timeout === 'object') {
        var enter = timeout.enter, exit = timeout.exit;
        return (typeof enter !== 'undefined' &&
            typeof exit !== 'undefined' &&
            (enter > 0 || exit > 0));
    }
    return !!timeout;
};
var getArrowShift = function (shift, direction) {
    if (!shift && !isTestEnv) {
        return {};
    }
    if (direction.startsWith('top') || direction.startsWith('bottom')) {
        return { left: shift + "px" };
    }
    if (direction.startsWith('left') || direction.startsWith('right')) {
        return { top: shift + "px" };
    }
    // Arrow can't be shifted when using automatic positioning
    return {};
};
/**
 * Popover
 */
var Popover = /** @class */ (function (_super) {
    tslib_1.__extends(Popover, _super);
    function Popover(props) {
        var _this = _super.call(this, props) || this;
        _this.targetRef = null;
        _this.portalNode = null;
        _this.appendToNode = null;
        _this.clickOutsideRef = null;
        _this.popperScheduleUpdate = null;
        // Timer instances for the show/hide delays
        _this._hideTimeout = null;
        _this._showTimeout = null;
        _this._handleClickOutside = function (event) {
            var _a = _this.props, onClickOutsideCallback = _a.onClickOutside, shown = _a.shown, disableClickOutsideWhenClosed = _a.disableClickOutsideWhenClosed;
            if (onClickOutsideCallback && !(disableClickOutsideWhenClosed && !shown)) {
                onClickOutsideCallback(event);
            }
        };
        _this.state = {
            isMounted: false,
            shown: props.shown || false,
        };
        if (isTestEnv) {
            testId = helpers_1.popoverTestUtils.generateId();
        }
        _this.clickOutsideRef = React.createRef();
        _this.clickOutsideClass = uniqueId('clickOutside');
        _this.contentHook = "popover-content-" + (props['data-hook'] || '') + "-" + testId;
        return _this;
    }
    Popover.prototype.getPopperContentStructure = function (childrenObject) {
        var _this = this;
        var _a = this.props, moveBy = _a.moveBy, appendTo = _a.appendTo, placement = _a.placement, showArrow = _a.showArrow, moveArrowTo = _a.moveArrowTo, flip = _a.flip, fixed = _a.fixed, customArrow = _a.customArrow, role = _a.role, id = _a.id, zIndex = _a.zIndex, minWidth = _a.minWidth, maxWidth = _a.maxWidth, width = _a.width, dynamicWidth = _a.dynamicWidth, _b = _a.excludeClass, excludeClass = _b === void 0 ? this.clickOutsideClass : _b, contentClassName = _a.contentClassName;
        var shouldAnimate = shouldAnimatePopover(this.props);
        var modifiers = modifiers_1.createModifiers({
            minWidth: minWidth,
            width: width,
            dynamicWidth: dynamicWidth,
            moveBy: moveBy,
            appendTo: appendTo,
            shouldAnimate: shouldAnimate,
            flip: flip,
            placement: placement,
            fixed: fixed,
            isTestEnv: isTestEnv,
        });
        var popper = (React.createElement(react_popper_1.Popper, { modifiers: modifiers, placement: placement }, function (_a) {
            var ref = _a.ref, popperStyles = _a.style, popperPlacement = _a.placement, arrowProps = _a.arrowProps, scheduleUpdate = _a.scheduleUpdate;
            _this.popperScheduleUpdate = scheduleUpdate;
            return (React.createElement(PopoverContext_1.PopoverContext.Consumer, null, function (_a) {
                var _b;
                var excludeClickOutsideClasses = _a.excludeClickOutsideClasses;
                return (React.createElement("div", { ref: ref, "data-hook": "popover-content", "data-content-element": _this.contentHook, style: tslib_1.__assign(tslib_1.__assign({}, popperStyles), { zIndex: zIndex, maxWidth: maxWidth }), "data-placement": popperPlacement || placement, className: classnames_1.default.apply(void 0, tslib_1.__spreadArrays([Popover_st_css_1.classes.popover,
                        _this.clickOutsideClass,
                        contentClassName, (_b = {},
                            _b[Popover_st_css_1.classes.withArrow] = showArrow,
                            _b[Popover_st_css_1.classes.popoverContent] = !showArrow,
                            _b)], excludeClickOutsideClasses)) },
                    showArrow &&
                        _this.renderArrow(arrowProps, moveArrowTo, popperPlacement || placement, customArrow),
                    React.createElement("div", { key: "popover-content", id: id, role: role, className: showArrow ? Popover_st_css_1.classes.popoverContent : '' },
                        React.createElement(PopoverContext_1.PopoverContext.Provider, { value: {
                                excludeClickOutsideClasses: tslib_1.__spreadArrays([
                                    excludeClass
                                ], excludeClickOutsideClasses),
                            } }, childrenObject.Content))));
            }));
        }));
        return this.wrapWithAnimations(popper);
    };
    Popover.prototype.applyStylesToPortaledNode = function () {
        var shown = this.state.shown;
        var shouldAnimate = shouldAnimatePopover(this.props);
        if (shouldAnimate || shown) {
            attachClasses(this.portalNode, this.portalClasses);
        }
        else {
            detachClasses(this.portalNode, this.portalClasses);
        }
    };
    Popover.prototype.wrapWithAnimations = function (popper) {
        var _this = this;
        var timeout = this.props.timeout;
        var shown = this.state.shown;
        var shouldAnimate = shouldAnimatePopover(this.props);
        return shouldAnimate ? (React.createElement(react_transition_group_1.CSSTransition, { in: shown, timeout: timeout, unmountOnExit: true, classNames: {
                enter: Popover_st_css_1.classes['popoverAnimation-enter'],
                enterActive: Popover_st_css_1.classes['popoverAnimation-enter-active'],
                exit: Popover_st_css_1.classes['popoverAnimation-exit'],
                exitActive: Popover_st_css_1.classes['popoverAnimation-exit-active'],
            }, onExited: function () { return detachClasses(_this.portalNode, _this.portalClasses); } }, popper)) : (popper);
    };
    Popover.prototype.renderPopperContent = function (childrenObject) {
        var popper = this.getPopperContentStructure(childrenObject);
        return this.portalNode ? (React.createElement(react_portal_1.Portal, { node: this.portalNode }, popper)) : (popper);
    };
    Popover.prototype.renderArrow = function (arrowProps, moveArrowTo, placement, customArrow) {
        var commonProps = {
            ref: arrowProps.ref,
            key: 'popover-arrow',
            'data-hook': 'popover-arrow',
            style: tslib_1.__assign(tslib_1.__assign({}, arrowProps.style), getArrowShift(moveArrowTo, placement)),
        };
        if (customArrow) {
            return customArrow(placement, commonProps);
        }
        return React.createElement("div", tslib_1.__assign({}, commonProps, { className: Popover_st_css_1.classes.arrow }));
    };
    Popover.prototype.componentDidMount = function () {
        this.initAppendToNode();
        this.setState({ isMounted: true });
    };
    Popover.prototype.initAppendToNode = function () {
        var appendTo = this.props.appendTo;
        this.appendToNode = getAppendToElement_1.getAppendToElement(appendTo, this.targetRef);
        if (this.appendToNode) {
            this.portalNode = document.createElement('div');
            this.portalNode.setAttribute('data-hook', 'popover-portal');
            /**
             * reset overlay wrapping layer
             * so that styles from copied classnames
             * won't break the overlay:
             * - content is position relative to body
             * - overlay layer is hidden
             */
            Object.assign(this.portalNode.style, {
                position: 'static',
                top: 0,
                left: 0,
                width: 0,
                height: 0,
            });
            this.appendToNode.appendChild(this.portalNode);
        }
    };
    Popover.prototype.hidePopover = function () {
        var _this = this;
        var isMounted = this.state.isMounted;
        var hideDelay = this.props.hideDelay;
        if (!isMounted || this._hideTimeout) {
            return;
        }
        if (this._showTimeout) {
            clearTimeout(this._showTimeout);
            this._showTimeout = null;
        }
        if (hideDelay) {
            this._hideTimeout = setTimeout(function () {
                _this.setState({ shown: false });
            }, hideDelay);
        }
        else {
            this.setState({ shown: false });
        }
    };
    Popover.prototype.showPopover = function () {
        var _this = this;
        var isMounted = this.state.isMounted;
        var showDelay = this.props.showDelay;
        if (!isMounted || this._showTimeout) {
            return;
        }
        if (this._hideTimeout) {
            clearTimeout(this._hideTimeout);
            this._hideTimeout = null;
        }
        if (showDelay) {
            this._showTimeout = setTimeout(function () {
                _this.setState({ shown: true });
            }, showDelay);
        }
        else {
            this.setState({ shown: true });
        }
    };
    Popover.prototype.componentWillUnmount = function () {
        if (this.portalNode && this.appendToNode.children.length) {
            // FIXME: What if component is updated with a different appendTo? It is a far-fetched use-case,
            // but we would need to remove the portaled node, and created another one.
            this.appendToNode.removeChild(this.portalNode);
        }
        this.portalNode = null;
        if (this._hideTimeout) {
            clearTimeout(this._hideTimeout);
            this._hideTimeout = null;
        }
        if (this._showTimeout) {
            clearTimeout(this._showTimeout);
            this._showTimeout = null;
        }
    };
    Popover.prototype.updatePosition = function () {
        if (this.popperScheduleUpdate) {
            this.popperScheduleUpdate();
        }
    };
    Popover.prototype.componentDidUpdate = function (prevProps) {
        var shown = this.props.shown;
        if (this.portalNode) {
            // Re-calculate the portal's styles
            this.portalClasses = Popover_st_css_1.st(Popover_st_css_1.classes.root, this.props.className);
            // Apply the styles to the portal
            this.applyStylesToPortaledNode();
        }
        // Update popover visibility
        if (prevProps.shown !== shown) {
            if (shown) {
                this.showPopover();
            }
            else {
                this.hidePopover();
            }
        }
        else {
            // Update popper's position
            this.updatePosition();
        }
    };
    Popover.prototype.render = function () {
        var _this = this;
        var _a = this.props, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onKeyDown = _a.onKeyDown, onClick = _a.onClick, children = _a.children, style = _a.style, id = _a.id, excludeClass = _a.excludeClass, fluid = _a.fluid;
        var _b = this.state, isMounted = _b.isMounted, shown = _b.shown;
        var childrenObject = utils_1.buildChildrenObject(children, {
            Element: null,
            Content: null,
        });
        var shouldAnimate = shouldAnimatePopover(this.props);
        var shouldRenderPopper = isMounted && (shouldAnimate || shown);
        return (React.createElement(react_popper_1.Manager, null,
            React.createElement(click_outside_1.ClickOutside, { rootRef: this.clickOutsideRef, onClickOutside: shown ? this._handleClickOutside : undefined, excludeClass: [this.clickOutsideClass, excludeClass] },
                React.createElement("div", tslib_1.__assign({ ref: this.clickOutsideRef, style: style, "data-content-hook": this.contentHook, className: Popover_st_css_1.st(Popover_st_css_1.classes.root, { fluid: fluid }, this.props.className), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, id: id }, filter_data_props_1.filterDataProps(this.props)),
                    React.createElement(react_popper_1.Reference, { innerRef: function (r) { return (_this.targetRef = r); } }, function (_a) {
                        var ref = _a.ref;
                        return (React.createElement("div", { ref: ref, className: Popover_st_css_1.classes.popoverElement, "data-hook": "popover-element", onClick: onClick, onKeyDown: onKeyDown }, childrenObject.Element));
                    }),
                    shouldRenderPopper && this.renderPopperContent(childrenObject)))));
    };
    Popover.displayName = 'Popover';
    Popover.defaultProps = {
        flip: true,
        fixed: false,
        zIndex: 1000,
    };
    Popover.Element = utils_1.createComponentThatRendersItsChildren('Popover.Element');
    Popover.Content = utils_1.createComponentThatRendersItsChildren('Popover.Content');
    return Popover;
}(React.Component));
exports.Popover = Popover;
//# sourceMappingURL=Popover.js.map